import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { 
  FaStar, 
  FaCheckCircle, 
  FaTimesCircle,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaCreditCard,
  FaMobileAlt,
  FaHeadset,
  FaGamepad,
  FaChartBar
} from 'react-icons/fa';

const offerData: Record<string, any> = {
  allbritishcasino: {
    name: 'All British Casino',
    image: '/images/offers/allbritishcasino.webp',
    bonus: '100% up to £100',
    rating: 4.8,
    minDeposit: '£10',
    wagering: '35x',
    maxBet: '£5',
    payoutTime: '24-48 hours',
    license: 'UK Gambling Commission',
    url: 'https://www.allbritishcasino.com/',
    features: [
      'Fast Payouts',
      'UK Licensed',
      'Live Casino',
      'Mobile Optimized',
      '24/7 Support',
      'SSL Encrypted'
    ],
    pros: [
      'Excellent customer support',
      'Fast withdrawal processing',
      'Wide game selection',
      'Regular promotions'
    ],
    cons: [
      'Limited payment methods',
      'Restricted in some countries'
    ],
    games: {
      slots: 500,
      live: 50,
      table: 30,
      jackpot: 20
    },
    paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller'],
    description: 'All British Casino offers a premium gaming experience with a focus on UK players. With a generous welcome bonus and excellent customer service, it\'s a top choice for both new and experienced players.'
  },
  partycasino: {
    name: 'PartyCasino',
    image: '/images/offers/partycasino.webp',
    bonus: '200% up to £200',
    rating: 4.7,
    minDeposit: '£20',
    wagering: '30x',
    maxBet: '£10',
    payoutTime: '12-24 hours',
    license: 'UK Gambling Commission',
    url: 'https://www.partycasino.com/',
    features: [
      'VIP Program',
      'Mobile App',
      'Tournaments',
      'Live Dealers',
      'Crypto Accepted',
      'Exclusive Games'
    ],
    pros: [
      'Outstanding VIP rewards',
      'Mobile app available',
      'Regular tournaments',
      'Crypto payment options'
    ],
    cons: [
      'Higher minimum deposit',
      'Stricter wagering requirements'
    ],
    games: {
      slots: 800,
      live: 80,
      table: 50,
      jackpot: 35
    },
    paymentMethods: ['Visa', 'Mastercard', 'PayPal', 'Bitcoin', 'Ethereum', 'Skrill'],
    description: 'PartyCasino brings the party to online gaming with exciting tournaments, a comprehensive VIP program, and a massive game library. Perfect for players who love competition and rewards.'
  },
  plazaroyal: {
    name: 'Plaza Royal',
    image: '/images/offers/plazaroyal.webp',
    bonus: '150% up to £150',
    rating: 4.6,
    minDeposit: '£15',
    wagering: '40x',
    maxBet: '£5',
    payoutTime: '24-72 hours',
    license: 'UK Gambling Commission',
    url: 'https://www.plazaroyal.com',
    features: [
      'Crypto Accepted',
      '24/7 Support',
      'Exclusive Games',
      'Fast Registration',
      'Multi-Currency',
      'Progressive Jackpots'
    ],
    pros: [
      'Cryptocurrency support',
      'Quick registration process',
      'Exclusive game titles',
      'Multi-currency accounts'
    ],
    cons: [
      'Higher wagering requirements',
      'Longer payout times'
    ],
    games: {
      slots: 600,
      live: 60,
      table: 40,
      jackpot: 25
    },
    paymentMethods: ['Visa', 'Mastercard', 'Bitcoin', 'Ethereum', 'Litecoin', 'PayPal'],
    description: 'Plaza Royal combines modern technology with classic casino elegance. With cryptocurrency support and exclusive games, it appeals to tech-savvy players seeking innovation.'
  }
};

const RatingBar = ({ label, value, max = 5 }: { label: string; value: number; max?: number }) => {
  const percentage = (value / max) * 100;
  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1">
        <span className="text-gray-300">{label}</span>
        <span className="text-white font-semibold">{value.toFixed(1)}/{max}</span>
      </div>
      <div className="w-full bg-dark-700 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const GameChart = ({ games }: { games: any }) => {
  const maxValue = Math.max(...Object.values(games) as number[]);
  
  return (
    <div className="bg-dark-800 rounded-xl p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-6 flex items-center">
        <FaChartBar className="mr-2 text-primary-400" />
        Game Library
      </h3>
      <div className="space-y-4">
        {Object.entries(games).map(([key, value]: [string, any]) => {
          const percentage = (value / maxValue) * 100;
          return (
            <div key={key}>
              <div className="flex justify-between mb-1">
                <span className="text-gray-300 capitalize">{key}</span>
                <span className="text-white font-semibold">{value}</span>
              </div>
              <div className="w-full bg-dark-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-primary-500 to-accent-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OfferDetail = () => {
  const { id } = useParams<{ id: string }>();
  const offer = id ? offerData[id] : null;

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Offer Not Found</h1>
          <Link to="/" className="text-primary-400 hover:text-primary-300">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${offer.name} - Casino Review`,
    description: offer.description,
    image: `https://spinemerge.com${offer.image}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: offer.rating,
      reviewCount: Math.floor(Math.random() * 500) + 100
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'GBP',
      price: '0',
      description: offer.bonus
    }
  };

  return (
    <>
      <SEO
        title={`${offer.name} Review & Bonus - ${offer.bonus}`}
        description={`Complete review of ${offer.name}. Get ${offer.bonus} welcome bonus. Read our expert analysis of games, bonuses, and features.`}
        canonical={`/offers/${id}`}
        jsonLd={jsonLd}
        ogImage={offer.image}
      />

      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-48 h-48 rounded-2xl object-contain border-4 border-primary-500/50"
                  loading="eager"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                  <h1 className="text-4xl md:text-5xl font-bold text-white">{offer.name}</h1>
                  <div className="flex items-center space-x-1 bg-dark-800 px-3 py-1 rounded-full">
                    <FaStar className="text-yellow-400" />
                    <span className="text-white font-semibold">{offer.rating}</span>
                  </div>
                </div>
                <p className="text-xl text-gray-300 mb-6">{offer.description}</p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="bg-primary-500/20 border border-primary-500/50 rounded-lg px-4 py-2">
                    <span className="text-primary-400 font-semibold">{offer.bonus}</span>
                  </div>
                  <div className="bg-dark-800 border border-gray-700 rounded-lg px-4 py-2">
                    <span className="text-gray-300">Min Deposit: {offer.minDeposit}</span>
                  </div>
                  <div className="bg-dark-800 border border-gray-700 rounded-lg px-4 py-2">
                    <span className="text-gray-300">Wagering: {offer.wagering}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Rating Breakdown */}
                <div className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                  <h2 className="text-2xl font-bold text-white mb-6">Rating Breakdown</h2>
                  <RatingBar label="Overall Rating" value={offer.rating} />
                  <RatingBar label="Game Selection" value={4.5} />
                  <RatingBar label="Bonuses & Promotions" value={4.7} />
                  <RatingBar label="Customer Support" value={4.6} />
                  <RatingBar label="Payment Speed" value={4.4} />
                  <RatingBar label="Mobile Experience" value={4.8} />
                </div>

                {/* Pros & Cons */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-dark-800 rounded-xl p-6 border border-green-500/30">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <FaCheckCircle className="text-green-400 mr-2" />
                      Pros
                    </h3>
                    <ul className="space-y-2">
                      {offer.pros.map((pro: string, i: number) => (
                        <li key={i} className="text-gray-300 flex items-start">
                          <FaCheckCircle className="text-green-400 mr-2 mt-1 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-dark-800 rounded-xl p-6 border border-red-500/30">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <FaTimesCircle className="text-red-400 mr-2" />
                      Cons
                    </h3>
                    <ul className="space-y-2">
                      {offer.cons.map((con: string, i: number) => (
                        <li key={i} className="text-gray-300 flex items-start">
                          <FaTimesCircle className="text-red-400 mr-2 mt-1 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Features */}
                <div className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <FaGift className="mr-2 text-primary-400" />
                    Key Features
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {offer.features.map((feature: string, i: number) => (
                      <div key={i} className="flex items-center text-gray-300">
                        <FaCheckCircle className="text-primary-400 mr-3 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <FaCreditCard className="mr-2 text-primary-400" />
                    Payment Methods
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {offer.paymentMethods.map((method: string, i: number) => (
                      <div
                        key={i}
                        className="bg-dark-900 border border-gray-700 rounded-lg px-4 py-2 text-gray-300"
                      >
                        {method}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Game Chart */}
                <GameChart games={offer.games} />

                {/* Quick Info */}
                <div className="bg-dark-800 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">Quick Info</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center">
                        <FaShieldAlt className="mr-2" />
                        License
                      </span>
                      <span className="text-white font-semibold">{offer.license}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center">
                        <FaClock className="mr-2" />
                        Payout Time
                      </span>
                      <span className="text-white font-semibold">{offer.payoutTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center">
                        <FaMobileAlt className="mr-2" />
                        Mobile
                      </span>
                      <span className="text-green-400 font-semibold">Yes</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center">
                        <FaHeadset className="mr-2" />
                        Support
                      </span>
                      <span className="text-white font-semibold">24/7</span>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white text-center font-bold py-4 rounded-xl hover:from-primary-600 hover:to-accent-600 transition-all duration-200 shadow-lg"
                >
                  Claim Bonus Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

